import { QuestionCard } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/QuestionsTab/components/Question/QuestionCard";
import { computeStatus } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/services";
import { AutosaveDisplay } from "@app/shared/components/AutosaveDisplay/AutosaveDisplay";
import { UploadedDocumentsList } from "@app/shared/components/documentUpload/UploadedDocumentsList";
import { type SupportedLocales } from "@app/shared/utils/locales";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Textarea } from "@design-system/Inputs/Textarea";
import { Textfield } from "@design-system/Inputs/Textfield";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { cn } from "@design-system/Utilities";
import {
    AnswerStatus,
    QuestionType,
    type CreateFileInput,
} from "@generated/client/graphql";
import { useMemo, useState, type FC } from "react";
import { allowProof, isProofMissing, isProofRequired } from "../../services";
import { type Question as TQuestion } from "../../types";
import { UploadDocumentModal } from "../UploadDocumentModal";
import { useAnswer } from "./hooks";
import { useSupplierQuestionnaireQuestionTranslations } from "./i18n";
export const Question: FC<{
    currentLocale: SupportedLocales;
    question: TQuestion;
    isOpen?: boolean;
}> = ({ currentLocale, question, isOpen = true }) => {
    const { t, keys } = useSupplierQuestionnaireQuestionTranslations();
    const options = useMemo(() => {
        return (
            question.metadata?.options?.map(
                (o) =>
                    o.name?.[currentLocale] ??
                    t(keys.option_non_available_in_locale),
            ) ?? []
        );
    }, [question.metadata?.options, currentLocale]);

    const {
        textAnswer,
        disableDataInputs,
        handleCommentBlur,
        handleCommentChange,
        handleValueChange,
        handleSingleSelectChange,
        handleMultiSelectChange,
        handleTextChange,
        handleTextOrValueBlur,
        handleUploadDocuments,
        handleRemoveDocument,
        isUpsertLoading,
        optimisticAnswer,
        showMissingCommentWarning,
        answerStatus,
        isCommentSectionOpen,
        isValidated,
        canBeValidated,
        canBeReset,
        handleNoteButtonClick,
        handleDeleteCommentClick,
        handleNoDataSwitchChange,
        handleValidationClick,
        handleResetValidationClick,
        handleResetClick,
    } = useAnswer(question, { isOpen, questionId: question.id });

    const [isUploadDocumentsModalOpen, setIsUploadDocumentsModalOpen] =
        useState(false);

    const openDocumentsModal = () => {
        setIsUploadDocumentsModalOpen(true);
    };

    const status = useMemo(() => {
        const status = computeStatus({
            status: optimisticAnswer?.status ?? AnswerStatus.NotAnswered,
            value: optimisticAnswer?.value ?? undefined,
        });

        return status;
    }, [
        optimisticAnswer?.status,
        optimisticAnswer?.value,
        optimisticAnswer?.comment,
    ]);

    const showMissingProofWarning = useMemo(() => {
        return isProofMissing(question);
    }, [question]);

    const isProofAllowed = useMemo(() => {
        return allowProof(question);
    }, [question]);

    const handleUploadDocumentModalConfirm = async (
        files?: CreateFileInput[],
    ) => {
        await handleUploadDocuments(files);
        setIsUploadDocumentsModalOpen(false);
    };

    return (
        <QuestionCard>
            <QuestionCard.Header>
                <FlexRow gap="2" alignItems="center">
                    <QuestionCard.StatusCol status={status} />
                    {isValidated ? (
                        <QuestionCard.LockedStatus />
                    ) : (
                        <AutosaveDisplay statuses={answerStatus} />
                    )}
                </FlexRow>
            </QuestionCard.Header>
            <QuestionCard.Content>
                <FlexCol className="pb-4 border-b border-tertiary">
                    <QuestionCard.Title
                        isRequired={question.required ?? false}
                        color={
                            question.nameMultiLocale?.[currentLocale]
                                ? "primary"
                                : "secondary"
                        }
                    >
                        {question.nameMultiLocale?.[currentLocale] ||
                            t(keys.unavailable_translation)}
                    </QuestionCard.Title>
                    {question.descriptionMultiLocale?.[currentLocale] && (
                        <QuestionCard.Description>
                            {question.descriptionMultiLocale?.[currentLocale]}
                        </QuestionCard.Description>
                    )}
                </FlexCol>
                {question.metadata?.enableNaOption !== false && (
                    <FlexRow gap="2" alignSelf="end">
                        <QuestionCard.Switch
                            checked={!!optimisticAnswer?.value?.na}
                            onChange={handleNoDataSwitchChange}
                            disabled={!isOpen || isValidated}
                            label={t(keys.no_data_switch_label)}
                        />
                        <Tooltip>
                            <Tooltip.Trigger>
                                <Icon name="circleInfo" size="sm" />
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                                <Text>{t(keys.no_data_switch_tooltip)}</Text>
                            </Tooltip.Content>
                        </Tooltip>
                    </FlexRow>
                )}

                <FlexRow
                    w="full"
                    alignItems="start"
                    justifyContent="between"
                    gap="4"
                >
                    {question.type === QuestionType.Text && (
                        <Textarea
                            disabled={disableDataInputs}
                            onChange={handleTextChange}
                            placeholder={t(keys.text_placeholder)}
                            value={optimisticAnswer?.value?.text ?? ""}
                            onBlur={handleTextOrValueBlur}
                        />
                    )}
                    {question.type === QuestionType.Value && (
                        <div className="w-full">
                            <Textfield
                                disabled={disableDataInputs}
                                type="number"
                                onChange={handleValueChange}
                                placeholder={
                                    question.metadata?.unit
                                        ? t(keys.value_unit_placeholder, {
                                              unit: question.metadata?.unit,
                                          })
                                        : t(keys.value_placeholder)
                                }
                                value={optimisticAnswer?.value?.num ?? ""}
                                onBlur={handleTextOrValueBlur}
                            />
                        </div>
                    )}
                    {question.type === QuestionType.SingleSelect && (
                        <QuestionCard.SingleSelect
                            disabled={disableDataInputs}
                            options={options}
                            value={optimisticAnswer?.value?.options?.[0]}
                            onChange={handleSingleSelectChange}
                        />
                    )}
                    {question.type === QuestionType.MultiSelect && (
                        <QuestionCard.MultiSelect
                            disabled={disableDataInputs}
                            options={options}
                            value={optimisticAnswer?.value?.options ?? []}
                            onChange={handleMultiSelectChange}
                        />
                    )}
                    <FlexCol
                        className={cn(
                            "grid grid-cols-2 gap-4",
                            (isValidated || !isOpen) && "flex",
                        )}
                    >
                        <QuestionCard.CopyButton
                            answer={textAnswer ?? undefined}
                        />
                        {!isValidated && isOpen && (
                            <QuestionCard.NoteButton
                                onClick={handleNoteButtonClick}
                            />
                        )}
                    </FlexCol>
                </FlexRow>

                {isCommentSectionOpen && (
                    <FlexRow
                        pl="4"
                        className="border-l border-secondary"
                        alignItems="center"
                        gap="4"
                    >
                        <Textarea
                            disabled={!isOpen || isValidated}
                            onChange={handleCommentChange}
                            placeholder={t(keys.comment_placeholder)}
                            value={optimisticAnswer?.comment ?? ""}
                            error={
                                showMissingCommentWarning
                                    ? t(keys.comment_error)
                                    : ""
                            }
                            onBlur={handleCommentBlur}
                        />
                        {!disableDataInputs && (
                            <Tooltip>
                                <Tooltip.Trigger>
                                    <Icon
                                        className="text-primary"
                                        name="trash"
                                        size="sm"
                                        onClick={handleDeleteCommentClick}
                                    />
                                </Tooltip.Trigger>
                                <Tooltip.Content>
                                    {t(keys.delete_comment)}
                                </Tooltip.Content>
                            </Tooltip>
                        )}
                    </FlexRow>
                )}

                {((!disableDataInputs && isProofAllowed) ||
                    !!question?.answer?.evidenceFiles?.length) && (
                    <FlexCol gap="1.5">
                        <FlexRow>
                            {isProofRequired(question) && (
                                <Text color="danger" variant="body-bold">
                                    *
                                </Text>
                            )}
                            <Text variant="body-bold">
                                {t(keys.evidence_files)}
                            </Text>
                        </FlexRow>
                        {!!question?.answer?.evidenceFiles?.length && (
                            <UploadedDocumentsList
                                files={question?.answer?.evidenceFiles ?? []}
                                onRemove={handleRemoveDocument}
                                disableDelete={disableDataInputs}
                            />
                        )}
                        {!disableDataInputs && (
                            <QuestionCard.UploadButton
                                onClick={openDocumentsModal}
                            />
                        )}
                        {showMissingProofWarning && (
                            <Text color="danger">
                                {t(keys.missing_proof_warning)}
                            </Text>
                        )}
                    </FlexCol>
                )}

                {isOpen && (
                    <FlexRow
                        w="full"
                        justifyContent="end"
                        className="border-t"
                        gap="1"
                        pt="4"
                    >
                        {isValidated ? (
                            <Button
                                size="md"
                                onClick={handleResetValidationClick}
                                variant="outlined"
                            >
                                {t(keys.reset_validation)}
                            </Button>
                        ) : (
                            <FlexRow alignItems="center" gap="4">
                                <Button
                                    size="md"
                                    variant="text"
                                    disabled={!canBeReset}
                                    onClick={handleResetClick}
                                >
                                    {t(keys.reset_answer)}
                                </Button>
                                <Tooltip delayDuration={0}>
                                    <Tooltip.Trigger asChild>
                                        <Box>
                                            <Button
                                                size="md"
                                                disabled={!canBeValidated}
                                                onClick={handleValidationClick}
                                            >
                                                {t(keys.validate_answer)}
                                            </Button>
                                        </Box>
                                    </Tooltip.Trigger>
                                    {!canBeValidated && (
                                        <Tooltip.Content className="max-w-[400px]">
                                            {t(keys.validate_answer_tooltip)}
                                        </Tooltip.Content>
                                    )}
                                </Tooltip>
                            </FlexRow>
                        )}
                    </FlexRow>
                )}
            </QuestionCard.Content>
            <UploadDocumentModal
                open={isUploadDocumentsModalOpen}
                onOpenChange={setIsUploadDocumentsModalOpen}
                onConfirm={handleUploadDocumentModalConfirm}
                loading={isUpsertLoading}
            />
        </QuestionCard>
    );
};
